import { useMemo, useState, useCallback } from 'react'

import type { CustomActionConfig } from '../types'
import type { ResponseData } from '@components/DynamicActions/hooks/useActionParams'

/**
 * Manages dialog state for custom actions supplied to `DynamicActions`.
 * It separates actions into visible (rendered as buttons) and hidden
 * (rendered in dropdown) based on `isDropdownHidden` flag, exposes
 * helpers to open/close dialogs and returns rendered `CustomDialogs`.
 *
 * Params:
 *  - customActions: Record<string, CustomActionConfig> - config keyed by action name
 *  - accountId?: number - optional id passed to custom components
 *  - onSuccess?: (res?: ResponseData) => void - global success callback
 *
 * Usage:
 *  const { visibleCustomActions, openDialog, CustomDialogs } = useCustomActions(customActions, userId, onSuccess)
 */
export const useCustomActions = (
  customActions: Record<string, CustomActionConfig> = {},
  accountId?: number | null,
  onSuccess?: (res?: ResponseData) => void,
) => {
  const [openDialogs, setOpenDialogs] = useState<Record<string, boolean>>({})

  const { visibleActions, hiddenActions } = useMemo(() => {
    const entries = Object.entries(customActions)

    const visible = Object.fromEntries(entries.filter(([, config]) => !config.isDropdownHidden))

    const hidden = Object.fromEntries(entries.filter(([, config]) => config.isDropdownHidden))

    return {
      visibleActions: visible,
      hiddenActions: hidden,
    }
  }, [customActions])

  const onOpenDialog = useCallback((key: string) => setOpenDialogs((prev) => ({ ...prev, [key]: true })), [])
  const onCloseDialog = useCallback((key: string) => setOpenDialogs((prev) => ({ ...prev, [key]: false })), [])

  const handleSuccess = useCallback(
    (key: string) => (res?: ResponseData) => {
      onSuccess?.(res)
      onCloseDialog(key)
    },
    [onSuccess, onCloseDialog],
  )

  const handleOpenChange = useCallback((key: string) => (open: boolean) => !open && onCloseDialog(key), [onCloseDialog])

  // Render all open dialogs (both visible and hidden custom actions)
  const CustomDialogs = useMemo(() => {
    return Object.entries(openDialogs)
      .filter(([_, isOpen]) => isOpen)
      .map(([actionName]) => {
        const config = customActions[actionName]

        if (!config) {
          return null
        }

        const Component = config.component

        return (
          <Component
            key={actionName}
            accountId={accountId}
            onSuccess={handleSuccess(actionName)}
            isOpenOutside={true}
            onOpenChange={handleOpenChange(actionName)}
            {...config.props}
          />
        )
      })
  }, [openDialogs, customActions, accountId, handleSuccess, handleOpenChange])

  return {
    visibleCustomActions: visibleActions,
    hiddenCustomActions: hiddenActions,
    hasVisibleCustomActions: Object.keys(visibleActions).length > 0,
    openDialog: onOpenDialog,
    closeDialog: onCloseDialog,
    CustomDialogs,
  }
}
